import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, Sparkles, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressLogo } from '@/lib/imageUtils';

const Welcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [countdown, setCountdown] = useState(10);
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(true);
  const [organizationCreated, setOrganizationCreated] = useState(false);

  // Get onboarding data from localStorage
  const getOnboardingData = () => {
    const stored = localStorage.getItem('onboarding-data');
    const data = stored ? JSON.parse(stored) : null;
    console.log('=== WELCOME PAGE DEBUG ===');
    console.log('Raw localStorage data:', stored);
    console.log('Parsed data:', data);
    console.log('organizationType is:', data?.organizationType);
    console.log('organizationType type:', typeof data?.organizationType);
    console.log('existingOrgId is:', data?.existingOrgId);
    console.log('Is existing org check:', data?.organizationType === 'existing');
    console.log('Has existingOrgId check:', !!data?.existingOrgId);
    return data;
  };

  const createOrganization = async () => {
    if (!user) return;

    try {
      const onboardingData = getOnboardingData();
      if (!onboardingData) {
        throw new Error('Keine Onboarding-Daten gefunden');
      }

      console.log('Processing onboarding with data:', onboardingData);
      console.log('Organization type:', onboardingData.organizationType);
      console.log('Existing org ID:', onboardingData.existingOrgId);

      // CASE 1: User is joining an existing organization
      if (onboardingData.organizationType === 'existing' && onboardingData.existingOrgId) {
        console.log('=== JOINING EXISTING ORGANIZATION ===');
        console.log('Joining organization ID:', onboardingData.existingOrgId);
        
        // Use the database function to assign user to correct team
        console.log('Using assign_user_to_team function for organization:', onboardingData.existingOrgId);
        
        const { data: assignedTeamId, error: assignError } = await supabase
          .rpc('assign_user_to_team', {
            user_id_param: user.id,
            org_id_param: onboardingData.existingOrgId
          });

        if (assignError) {
          console.error('Error assigning user to team:', assignError);
          throw assignError;
        }

        // Update user profile to complete onboarding (no longer setting admin_role)
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            organization_id: onboardingData.existingOrgId,
            onboarding_completed: true
          })
          .eq('user_id', user.id);

        if (profileError) throw profileError;
        
        // Get team name for logging
        const { data: teamData } = await supabase
          .from('teams')
          .select('name')
          .eq('id', assignedTeamId)
          .single();
        
        console.log('Successfully joined existing organization with team:', teamData?.name);
        
        // Clear onboarding data from localStorage
        localStorage.removeItem('onboarding-data');
        localStorage.removeItem('onboarding-step');
        
        toast.success('Erfolgreich der Organisation beigetreten!');
        
        // Navigate directly to homepage after successful completion
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1000);
        return;
      }

      // CASE 2: User is creating a new organization (Vodafone or Custom)
      console.log('=== CREATING NEW ORGANIZATION ===');
      console.log('Organization type:', onboardingData.organizationType);

      // Handle logo upload if exists
      let logoUrl = null;
      if (onboardingData.logoFile) {
        console.log('Uploading logo...');
        // Compress and resize logo before upload
        const compressedLogo = await compressLogo(onboardingData.logoFile);
        const fileName = `${Date.now()}-${compressedLogo.name}`;
        
        const { error: uploadError } = await supabase.storage
          .from('organization-logos')
          .upload(fileName, compressedLogo);

        if (uploadError) {
          console.error('Logo upload error:', uploadError);
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('organization-logos')
            .getPublicUrl(fileName);

          logoUrl = publicUrl;
          console.log('Logo uploaded successfully:', logoUrl);
        }
      }

      // Only check for duplicates when creating a NEW custom organization
      if (onboardingData.organizationType === 'custom') {
        console.log('Checking for duplicate organization names...');
        const { data: existingOrg, error: checkError } = await supabase
          .from('organizations')
          .select('id, name')
          .ilike('name', onboardingData.organizationName)
          .limit(1);

        if (checkError) throw checkError;

        if (existingOrg && existingOrg.length > 0) {
          console.log('Organization already exists, redirecting back to onboarding');
          toast.error(`Eine Organisation mit dem Namen "${onboardingData.organizationName}" existiert bereits. Bitte wählen Sie einen anderen Namen oder treten Sie der bestehenden Organisation bei.`);
          setIsCreatingOrganization(false);
          setTimeout(() => {
            navigate('/onboarding');
          }, 2000);
          return;
        }
      }

      // Create organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: onboardingData.organizationName,
          type: onboardingData.organizationType,
          color: onboardingData.color,
          sales_name: onboardingData.salesName,
          logo_url: logoUrl,
          created_by: user.id
        })
        .select()
        .single();

      if (orgError) {
        // Handle case where unique constraint was violated
        if (orgError.code === '23505') {
          console.log('Organization already exists (constraint violation), redirecting back to onboarding');
          toast.error(`Eine Organisation mit dem Namen "${onboardingData.organizationName}" existiert bereits. Bitte wählen Sie einen anderen Namen oder treten Sie der bestehenden Organisation bei.`);
          setIsCreatingOrganization(false);
          setTimeout(() => {
            navigate('/onboarding');
          }, 2000);
          return;
        }
        throw orgError;
      }

      const organization = orgData;
      console.log('Organization created successfully:', organization);

      // Create initial tasks if provided
      if (onboardingData.initialTasks && onboardingData.initialTasks.length > 0) {
        console.log('Creating initial tasks...');
        const { error: tasksError } = await supabase
          .from('tasks')
          .insert(
            onboardingData.initialTasks.map((task: any) => ({
              user_id: user.id,
              organization_id: organization.id,
              title: task.title,
              description: task.description,
              color: task.color,
              reminder_interval_days: task.intervalDays,
              reminder_interval_hours: task.intervalHours,
              completed: false
            }))
          );

        if (tasksError) throw tasksError;
        console.log('Tasks created successfully');
      }

      // NOW update user profile with organization and mark onboarding as complete
      const profileUpdate: any = {
        organization_id: orgData.id,
        onboarding_completed: true
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdate)
        .eq('user_id', user.id);

      if (profileError) throw profileError;
      console.log('Profile updated successfully - onboarding completed');
      
      // Add admin role to user_roles table for organization creator
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: user.id,
          role: 'admin'
        });
      
      if (roleError) {
        console.error('Error adding admin role:', roleError);
        // Don't throw - profile update succeeded
      } else {
        console.log('Admin role added successfully');
      }

      // Clear onboarding data from localStorage
      localStorage.removeItem('onboarding-data');
      localStorage.removeItem('onboarding-step');

      toast.success('Organisation erfolgreich erstellt!');
      
      // Navigate directly to homepage after successful completion
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1000);

    } catch (error: any) {
      console.error('Error creating organization:', error);
      toast.error('Fehler beim Erstellen der Organisation: ' + error.message);
      setIsCreatingOrganization(false);
      // Navigate back to onboarding on error (don't clear localStorage here)
      setTimeout(() => {
        navigate('/onboarding');
      }, 3000);
    }
  };

  useEffect(() => {
    if (user && isCreatingOrganization) {
      createOrganization();
    }
  }, [user]);

  useEffect(() => {
    if (organizationCreated) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [organizationCreated, navigate]);

  const handleSkipCountdown = () => {
    if (organizationCreated) {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="text-center shadow-lg border bg-card">
          <CardContent className="p-8">
            {isCreatingOrganization ? (
              <>
                {/* Creating Organization Animation */}
                <div className="relative mb-6">
                  <div className="mx-auto w-20 h-20 bg-primary rounded-full flex items-center justify-center">
                    <Building2 className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  </div>
                </div>

                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Organisation wird eingerichtet...
                </h1>
                
                <p className="text-muted-foreground mb-6">
                  Bitte warten Sie, während wir Ihre Organisation einrichten oder Sie der ausgewählten Organisation hinzufügen.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center justify-center gap-3 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Organisation wird bearbeitet...</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-muted rounded-full"></div>
                    <span>Profile werden konfiguriert...</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-muted rounded-full"></div>
                    <span>Aufgaben werden erstellt...</span>
                  </div>
                </div>

                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: "65%" }}></div>
                </div>
              </>
            ) : organizationCreated ? (
              <>
                {/* Success Animation */}
                <div className="relative mb-6">
                  <div className="mx-auto w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-yellow-400 animate-bounce" />
                  </div>
                </div>

                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Willkommen im Team! 🎉
                </h1>
                
                <p className="text-muted-foreground mb-6">
                  Ihre Organisation wurde erfolgreich eingerichtet. Sie werden in {countdown} Sekunden zur Hauptseite weitergeleitet.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center justify-center gap-3 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Organisation erstellt</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Profile konfiguriert</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Aufgaben erstellt</span>
                  </div>
                </div>

                <Button 
                  onClick={handleSkipCountdown}
                  className="w-full"
                >
                  Jetzt starten <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                {/* Countdown Circle */}
                <div className="flex justify-center mt-6">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 rotate-[-90deg]" viewBox="0 0 32 32">
                      <circle
                        cx="16"
                        cy="16"
                        r="14"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        className="text-muted"
                      />
                      <circle
                        cx="16"
                        cy="16"
                        r="14"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray={`${(countdown / 10) * 88} 88`}
                        className="text-primary transition-all duration-1000 ease-linear"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold">{countdown}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Error State */}
                <div className="text-center">
                  <div className="mx-auto w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mb-6">
                    <Building2 className="h-10 w-10 text-white" />
                  </div>
                  
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    Ein Fehler ist aufgetreten
                  </h1>
                  
                  <p className="text-muted-foreground mb-6">
                    Es gab ein Problem beim Einrichten Ihrer Organisation. Sie werden zum Onboarding zurückgeleitet.
                  </p>
                  
                  <Button onClick={() => navigate('/onboarding')} className="w-full">
                    Zurück zum Onboarding
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Welcome;